import { v4 as uuidv4 } from 'uuid'
import { roofStoreyKey } from '@editor/stores/modelStore/constants'
import type {
  CustomPlanTableCreateDTO,
  PlanTableCreateDTO,
  PlanTableDTO,
  PlanTableModifyDTO,
  TemplatePlanTableCreateDTO,
} from '@mutations'
import {
  ASSEMBLIES_PLAN_TABLE_TEMPLATE_KEY,
  defaultPositionListTemplateOptions,
  defaultShearAnchorsTemplateOptions,
  defaultTensileAnchorsTemplateOptions,
  POSITION_LIST_PLAN_TABLE_TEMPLATE_KEY,
  SHEAR_ANCHORS_PLAN_TABLE_TEMPLATE_KEY,
  TENSILE_ANCHORS_PLAN_TABLE_TEMPLATE_KEY,
} from 'src/domain/planTables/constants'
import { createDefaultPlanTableData } from 'src/domain/planTables/defaults'
import { DEFAULT_PLAN_TABLE_POSITION_ANCHOR } from 'src/domain/planTables/positionAnchor'
import type {
  PlanTable,
  PlanTableInput,
  PlanTableTemplateOptions,
  PlanTapelineConfig,
} from 'src/domain/planTables/types'
import { isTemplatePlanTable } from 'src/domain/planTables/types'

export const DEFAULT_PLAN_TABLE_NAME = 'Tabelle'

export const DEFAULT_PLAN_CONFIG = {
  storeysForPlans: [] as string[],
  planTypes: [] as PlanTable['planConfig']['planTypes'],
}

const sortedNonRoofStoreys = (availableStoreys: Iterable<string>): string[] =>
  [...new Set(availableStoreys)]
    .filter(storey => storey !== roofStoreyKey)
    .sort((a, b) => Number(a) - Number(b))

const storeysForPlansFromAvailable = (availableStoreys: Iterable<string>): string[] => [
  ...sortedNonRoofStoreys(availableStoreys),
  roofStoreyKey,
]

// These tables belong on the stiffening plans, which have no roof storey.
const STIFFENING_PLAN_TABLE_TEMPLATE_KEYS = [
  ASSEMBLIES_PLAN_TABLE_TEMPLATE_KEY,
  TENSILE_ANCHORS_PLAN_TABLE_TEMPLATE_KEY,
  SHEAR_ANCHORS_PLAN_TABLE_TEMPLATE_KEY,
]

export const defaultPlanConfigForTemplate = (
  templateKey: string | null | undefined,
  availableStoreys: Iterable<string>,
): PlanTapelineConfig => {
  if (templateKey === POSITION_LIST_PLAN_TABLE_TEMPLATE_KEY) {
    return {
      storeysForPlans: storeysForPlansFromAvailable(availableStoreys),
      planTypes: ['PositionPlan'],
    }
  }

  if (templateKey && STIFFENING_PLAN_TABLE_TEMPLATE_KEYS.includes(templateKey)) {
    return {
      storeysForPlans: sortedNonRoofStoreys(availableStoreys),
      planTypes: ['StiffeningPlan'],
    }
  }

  return {
    storeysForPlans: [...DEFAULT_PLAN_CONFIG.storeysForPlans],
    planTypes: [...DEFAULT_PLAN_CONFIG.planTypes],
  }
}

export const defaultTemplateOptionsForTemplate = (
  templateKey?: string | null,
): PlanTableTemplateOptions => {
  if (templateKey === POSITION_LIST_PLAN_TABLE_TEMPLATE_KEY) {
    return defaultPositionListTemplateOptions()
  }
  if (templateKey === TENSILE_ANCHORS_PLAN_TABLE_TEMPLATE_KEY) {
    return defaultTensileAnchorsTemplateOptions()
  }
  if (templateKey === SHEAR_ANCHORS_PLAN_TABLE_TEMPLATE_KEY) {
    return defaultShearAnchorsTemplateOptions()
  }

  return {}
}

export const completePlanTableInput = (
  input: PlanTableInput,
  activeFilterViewId: string,
): PlanTable => {
  const base = {
    id: input.id || uuidv4(),
    name: input.name ?? DEFAULT_PLAN_TABLE_NAME,
    position: input.position,
    positionAnchor: input.positionAnchor ?? DEFAULT_PLAN_TABLE_POSITION_ANCHOR,
    filterViewId: input.filterViewId || activeFilterViewId,
    table: input.table ?? createDefaultPlanTableData(),
    isVisible: input.isVisible ?? true,
    isActive: input.isActive ?? true,
    planConfig: input.planConfig ?? { ...DEFAULT_PLAN_CONFIG, planTypes: [], storeysForPlans: [] },
    templateOptions: input.templateOptions ?? defaultTemplateOptionsForTemplate(input.templateKey),
    dateCreated: input.dateCreated ?? new Date().toISOString(),
    dateModified: input.dateModified ?? new Date().toISOString(),
  }

  if (input.tableType === 'TemplatePlanTable' && input.templateKey) {
    return {
      ...base,
      tableType: 'TemplatePlanTable',
      templateKey: input.templateKey,
    }
  }

  return {
    ...base,
    tableType: 'CustomPlanTable',
    templateKey: null,
  }
}

export const planTableDtoToPlanTable = (dto: PlanTableDTO): PlanTable => {
  const base = {
    id: dto.id,
    name: dto.name,
    position: dto.position,
    positionAnchor: dto.positionAnchor,
    filterViewId: dto.filterViewId,
    table: dto.table,
    isVisible: dto.isVisible,
    isActive: dto.isActive,
    planConfig: dto.planConfig,
    templateOptions: dto.templateOptions ?? {},
    dateCreated: dto.dateCreated,
    dateModified: dto.dateModified,
  }

  if (dto.tableType === 'TemplatePlanTable' && dto.templateKey) {
    return {
      ...base,
      tableType: 'TemplatePlanTable',
      templateKey: dto.templateKey,
    }
  }

  return {
    ...base,
    tableType: 'CustomPlanTable',
    templateKey: null,
  }
}

export const planTableToCreateDTO = (planTable: PlanTable): PlanTableCreateDTO => {
  const base = {
    id: planTable.id,
    name: planTable.name,
    position: planTable.position,
    positionAnchor: planTable.positionAnchor,
    filterViewId: planTable.filterViewId,
    isVisible: planTable.isVisible,
    isActive: planTable.isActive,
    planConfig: planTable.planConfig,
  }

  if (isTemplatePlanTable(planTable)) {
    const dto: TemplatePlanTableCreateDTO = {
      ...base,
      tableType: 'TemplatePlanTable',
      templateKey: planTable.templateKey,
      templateOptions: planTable.templateOptions ?? {},
    }
    return dto
  }

  const dto: CustomPlanTableCreateDTO = {
    ...base,
    tableType: 'CustomPlanTable',
    table: planTable.table,
  }
  return dto
}

export const planTableToModifyDTO = (planTable: PlanTable): PlanTableModifyDTO => {
  const base = {
    name: planTable.name,
    position: planTable.position,
    positionAnchor: planTable.positionAnchor,
    filterViewId: planTable.filterViewId,
    isVisible: planTable.isVisible,
    isActive: planTable.isActive,
    planConfig: planTable.planConfig,
  }

  if (isTemplatePlanTable(planTable)) {
    return {
      ...base,
      tableType: 'TemplatePlanTable',
      templateKey: planTable.templateKey,
      templateOptions: planTable.templateOptions ?? {},
    }
  }

  return {
    ...base,
    tableType: 'CustomPlanTable',
    table: planTable.table,
  }
}
